import PropTypes from 'prop-types'
import React, { Component } from 'react'
import moment from 'moment'
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl'
import { get } from 'lodash'
import { connect } from 'react-redux'
import { compose } from 'recompose'

import { wizard } from 'helpers/decorators'
import { ConfigurationFile } from 'containers/misc'
import { Account as AccountForm } from 'components/forms'
import { Checkbox } from 'components/fields'
import { Button, FormDebug, Note, Title } from 'components/misc'
import { apiParticipants, apiConfiguration } from 'helpers/decorators/api'
import { CONFIGURATION_KEYS, PAYMENT_TYPES } from 'constants/enums'
import validate from './account.validation'
import getParticipantAge from 'helpers/getParticipantAge'
import config from 'config'
import queryString from 'query-string'
import { notification } from 'redux/modules/app'

const enhance = compose(
  apiParticipants(),
  apiConfiguration(),
  connect((state, props) => {
    const policyTextObj = get(
      props,
      `configuration.values[${CONFIGURATION_KEYS.privacyPolicyText.id}]`
    )

    return {
      registrationInfo: get(state.api, 'participants.registrationInfo'),
      hasPolicy: get(
        props,
        `configuration.values[${CONFIGURATION_KEYS.privacyPolicyCheck.id}].booleanValue`,
        false
      ),
      policyText: get(policyTextObj, 'translatedText') ||
        get(policyTextObj, 'textValue')
    }
  }, {
    notification
  }),
  wizard({
    form: 'participantRegistration',
    multipage: true,
    validate,
    destroyOnUnmount: false
  })
)

class Account extends Component {
  static propTypes = {
    reset: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    createParticipant: PropTypes.func.isRequired,
    values: PropTypes.object,
    configuration: PropTypes.object,
    errors: PropTypes.object,
    participants: PropTypes.object,
    onGetConfigurationValue: PropTypes.func,
    hasPolicy: PropTypes.bool,
    push: PropTypes.func,
    policyText: PropTypes.string,
    registrationInfo: PropTypes.object,
    createParticipantUsingAuthCode: PropTypes.func.isRequired,
    notification: PropTypes.func.isRequired
  }

  componentDidMount() {
    Promise.all([
      this.props.onGetConfigurationValue(
        CONFIGURATION_KEYS.privacyPolicyCheck.id,
        get(this.props.values, 'organizationId')
      ),
      this.props.onGetConfigurationValue(
        CONFIGURATION_KEYS.privacyPolicyText.id,
        get(this.props.values, 'organizationId')
      )
    ])
  }

  handleIsAdult = (values, age) =>
    (get(values, 'helpers.price') === PAYMENT_TYPES.online.id ||
      get(values, 'helpers.price') === PAYMENT_TYPES.payPal.id ||
      get(values, 'helpers.price') === PAYMENT_TYPES.globalPayments.id ||
      get(values, 'helpers.price') === PAYMENT_TYPES.globalPaymentsWebpay.id) &&
    age >= getParticipantAge(this.props.configuration)

  submitParticipant = (fields, isAdultFlow, registrationInfo, authorizationCode) => {

    fields = {
      ...fields,
      isInvited: get(registrationInfo, 'isInvited')
    }

    const createParticipantPromise = (registrationInfo && authorizationCode)
      ? this.props.createParticipantUsingAuthCode(authorizationCode, { ...fields, noSuccessHandler: isAdultFlow })
      : this.props.createParticipant(fields, { noSuccessHandler: isAdultFlow })

    return createParticipantPromise
      .then(result => {
        if (isAdultFlow) {
          const authCode = get(
            result,
            'awards[0].participantRegistration.onlinePaymentAuthorizationCode'
          )
          const orgId = get(result, 'awards[0].organization.id')

          this.props.reset()

          if (get(fields, 'helpers.price') === PAYMENT_TYPES.online.id) {
            return this.props.push(`${config.publicPath}/mpg-init/` + authCode)
          }

          if (get(fields, 'helpers.price') === PAYMENT_TYPES.payPal.id) {
            return this.props.push(`${config.publicPath}/paypal-init/` + authCode)
          }

          if (get(fields, 'helpers.price') === PAYMENT_TYPES.globalPayments.id) {
            return this.props.push(`${config.publicPath}/gp-init/` + orgId + '/' + authCode)
          }

          if (get(fields, 'helpers.price') === PAYMENT_TYPES.globalPaymentsWebpay.id) {
            return this.props.push(`${config.publicPath}/gp-wp-init/${authCode}`)
          }
        }

        this.props.onSubmit()
        this.props.reset()
      })
      .catch(error => {
        if (error.body.errorCode === 'DUPLICATE_USERNAME') {
          this.props.notification('The username already exists', {
            intlKey: 'notifications.registration_duplicate_username',
            status: 'error'
          })
        } else {
          console.debug(error)
        }
      })
  };

  handleSubmit = fields => {
    const age = moment().diff(get(fields, 'birthDate'), 'years')

    const isAdultFlow = this.handleIsAdult(fields, age)

    const query = queryString.parse(get(this.props, 'location.search'))
    const authorizationCode = get(query, 'authorizationCode')
    const registrationInfo = get(this.props, 'registrationInfo')

    return this.submitParticipant(fields, isAdultFlow, registrationInfo, authorizationCode)
  }

  render() {
    const {
      handleSubmit,
      values,
      errors,
      participants,
      hasPolicy,
      policyText
    } = this.props

    const isPayingOnline = this.handleIsAdult(values, get(values, 'birthDate'))

    return (
      <form
        onSubmit={handleSubmit(this.handleSubmit)}
        data-testId="registrationAccountForm"
      >
        <Title h1>
          <FormattedMessage
            id="moduleTitle.online_record_book_account"
            defaultMessage="Online Record Book Account"
          />
        </Title>

        <Note lighter margin="0 0 35px 0">
          <FormattedHTMLMessage
            id="label.to_log_your_process_on_orb"
            defaultMessage="To log your process on the Online Record Book (ORB) you need a personal ORB account.<br />Please create your details here."
          />
        </Note>

        <AccountForm values={values} />

        <ConfigurationFile
          configKey={CONFIGURATION_KEYS.termsAndConditions.id}
          organizationId={get(values, 'organizationId')}
          isPublic
          formattedName={
            <FormattedMessage
              id="button.terms_and_conditions"
              defaultMessage="International Terms & Conditions"
            />
          }
        />

        <div className="row">
          <div className="col-xs-12">
            {hasPolicy &&
              <div>
                {policyText &&
                  <div
                    className="terms-box"
                    dangerouslySetInnerHTML={{ __html: policyText }}
                  />}
                <Checkbox
                  name="helpers.agreementPrivacyPolicy"
                  label="I have read the Privacy Policy and agree with them."
                  intlKey="label.i_have_read_privacy_policy"
                />
              </div>}

            <Checkbox
              name="helpers.terms"
              label="I have read the <a href='{url}' target='_blank'>Terms and conditions and agree with them."
              intlKey="label.i_have_read_the_terms_and_conditions"
              intlValues={{
                url: '/orb_parental_consent_form.pdf'
              }}
              dataTestId="termsAccountSelector"
            />
            <Checkbox
              name="helpers.photosConsent"
              label="You can use my photos for promotional purposes. We will always contact you before using your materials. For more information click here."
              intlKey="label.you_can_use_my_photos_for_promotional_purposes"
            />
          </div>
        </div>

        <FormDebug values={values} errors={errors} />

        <Button disabled={get(participants, 'loading')}>
          {isPayingOnline
            ? <FormattedMessage
                id="button.finish_and_pay"
                defaultMessage="Finish and Pay"
            />
            : <FormattedMessage id="button.submit" defaultMessage="Submit" />}
        </Button>
      </form>
    )
  }
}

export default enhance(Account)
